import type { Command } from "../document/commands.ts";
import type { MapDocument, PropBehaviour, PropInstance, TileRef, TilesetRef } from "../document/types.ts";
import { propsBehaviourCommand, selectedProps } from "./many-props.ts";
import { templateIdFor, templateNameProblem, type PropRef } from "./templates.ts";

/**
 * Behaviour presets (P1.6-T34, ADR-070's amendment "behaviour presets"): a behaviour configuration saved under
 * a name from a configured prop, with the frame it was configured on, and applied to selected props whose
 * frame fits. A frame fits when it has the preset frame's width and height, the rule the frame picker uses
 * for a state's frame, since the behaviour's state frames rest where the placed frame is drawn and its
 * activation cells are counted from that frame's bottom-left. Nothing here knows what a behaviour means.
 */

/**
 * A behaviour preset: its name, the frame of the prop it was saved from and that prop's behaviour whole.
 */
export interface BehaviourPreset {
  readonly name: string;
  readonly frame: TileRef;
  readonly behaviour: PropBehaviour;
}

/**
 * A frame's width and height in pixels, or undefined when it cannot be sized.
 */
export type PresetFrameSize = (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;

/**
 * What applying a preset to a selection does: the command (null when nothing changes), the selected props
 * whose frame fits and those left out because it does not, each in the selection's order.
 */
export interface PresetApplication {
  readonly command: Command | null;
  readonly fitting: readonly PropRef[];
  readonly leftOut: readonly PropRef[];
}

/**
 * What is wrong with a preset name, if anything: the template name rule, said as a preset's.
 *
 * @param name The name.
 * @returns The problem, or null for a good name.
 */
export function presetNameProblem(name: string): string | null {
  return templateNameProblem(name, "preset");
}

/**
 * The id a preset's file takes, made from its name as a template's is.
 *
 * @param name The preset's name.
 * @param taken The ids other presets hold.
 * @returns The id.
 */
export function presetIdFor(name: string, taken: Iterable<string>): string {
  return templateIdFor(name, taken);
}

/**
 * A copy of a behaviour sharing nothing with it; a behaviour is plain JSON data.
 *
 * @param behaviour The behaviour.
 * @returns The copy.
 */
function copied(behaviour: PropBehaviour): PropBehaviour {
  return JSON.parse(JSON.stringify(behaviour)) as PropBehaviour;
}

/**
 * The preset a configured prop gives: the name, the prop's frame and a copy of its behaviour.
 *
 * @param prop The prop.
 * @param name The preset's name.
 * @returns The preset.
 * @throws Error when the name is bad or the prop carries no behaviour.
 */
export function capturePreset(prop: PropInstance, name: string): BehaviourPreset {
  const problem = presetNameProblem(name);
  if (problem !== null) {
    throw new Error(problem);
  }
  if (prop.behaviour === undefined) {
    throw new Error(`prop ${prop.id} carries no behaviour to save as a preset`);
  }
  return { name, frame: { tileset: prop.frame.tileset, tile: prop.frame.tile }, behaviour: copied(prop.behaviour) };
}

/**
 * Whether a frame fits a preset: it has the preset frame's width and height. A frame, or a preset frame, that
 * cannot be sized does not fit.
 *
 * @param preset The preset.
 * @param frame The frame.
 * @param sizeOf Frame sizes.
 * @returns True when it fits.
 */
export function presetFits(preset: BehaviourPreset, frame: TileRef, sizeOf: PresetFrameSize): boolean {
  const want = sizeOf(preset.frame);
  const size = sizeOf(frame);
  return want !== undefined && size !== undefined && want.w === size.w && want.h === size.h;
}

/**
 * Why a preset cannot be applied in a world (flagged, never dropped): every frame it names (its own, its state
 * frames, its strips) on a tileset the world does not list or missing from the editor's atlas, each problem
 * once, then a behaviour type the game does not register.
 *
 * @param preset The preset.
 * @param tilesets The world's tilesets.
 * @param frameExists Whether the editor's atlas holds a frame.
 * @param objectTypes The object type ids the game registers.
 * @returns The problems, empty when it can be applied.
 */
export function presetIssues(preset: BehaviourPreset, tilesets: readonly TilesetRef[], frameExists: (frame: TileRef) => boolean, objectTypes: readonly string[]): string[] {
  const listed = new Set(tilesets.map((t) => t.id));
  const issues: string[] = [];
  for (const frame of [preset.frame, ...Object.values(preset.behaviour.frames ?? {}), ...Object.values(preset.behaviour.animations ?? {})]) {
    const message = !listed.has(frame.tileset) ? `tileset ${frame.tileset} is not in this world` : frameExists(frame) ? null : `${frame.tileset} tile ${frame.tile} is not in the catalogue`;
    if (message !== null && !issues.includes(message)) {
      issues.push(message);
    }
  }
  if (!objectTypes.includes(preset.behaviour.type)) {
    issues.push(`behaviour type ${preset.behaviour.type} is not registered`);
  }
  return issues;
}

/**
 * Applies a preset to a selection: every selected prop the map holds whose frame fits takes a copy of the
 * preset's behaviour, as one command, a prop already carrying an equal one left out of it; the others are
 * left out and listed.
 *
 * @param map The map.
 * @param refs The selection.
 * @param preset The preset.
 * @param sizeOf Frame sizes.
 * @returns The command and the props it fits and leaves out.
 */
export function presetApplication(map: MapDocument, refs: readonly PropRef[], preset: BehaviourPreset, sizeOf: PresetFrameSize): PresetApplication {
  const fitting: PropRef[] = [];
  const leftOut: PropRef[] = [];
  for (const { layerId, prop } of selectedProps(map, refs)) {
    (presetFits(preset, prop.frame, sizeOf) ? fitting : leftOut).push({ layerId, propId: prop.id });
  }
  return { command: propsBehaviourCommand(map, fitting, () => copied(preset.behaviour)), fitting, leftOut };
}
